//eslint:disable max-func-body-length
import { CustomFilterType, Filter, FilterCommonValues, FilterType, PredefinedFilter, PredefinedFilterType,
  PredefinedIds } from 'app/area/search-filters/filters/types';
import { filterId, fieldType, allPredefinedFilters } from 'app/area/search-filters/constants';
import { CodetableTranslation } from 'app/area/indicator-factors/constants';
import { endpoints } from 'app/area/indicator-factors/common/endpoints';
import { codePickersId } from 'app/area/indicator-factors/item/code-pickers/types';
import { CodetableMetadata } from 'app/area/indicator-factors/types-store';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';

export const codePredefinedFilters = (translation: CodetableTranslation): PredefinedFilter => ({
  type: PredefinedFilterType.code,
  values: [
    { value: allPredefinedFilters.hideHistoricalFactors, description: translation.hide_historical_factors }
  ]
});

export const codeFilters = (metadata: CodetableMetadata, translation: CodetableTranslation): Filter[] => [
  {
    id: filterId.indicatorType,
    label: translation.indicatorType,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeWithoutExclude,
        optionsEndpoint: endpoints.optionsCollectionAll(codePickersId.indicatorType),
        optionsParams: { displayNotSelected: false }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.validFrom,
    label: translation.validFrom,
    fields: [
      {
        fieldTypeId: fieldType.date,
        type: CustomFilterType.dateSpecificPeriod,
        includeInSummary: true
      }
    ],
    type: FilterType.DATE_RANGE
  },
  {
    id: filterId.substanceCategory,
    label: translation.substanceCategory,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeWithoutExclude,
        optionsEndpoint: endpoints.optionsCollectionAll(codePickersId.substanceCategory),
        optionsParams: { displayNotSelected: false }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.substance,
    label: translation.substance,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeHierarchySubLevelWithCategoryHierarchical,
        categoryOptionsEndpoint: endpoints.optionsCollectionAll(codePickersId.substanceCategory),
        categoryOptionsParams: { displayNotSelected: false },
        optionsEndpoint: endpoints.optionsCollectionAll(codePickersId.substance),
        optionsParams: { displayNotSelected: false }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.substanceUom,
    label: translation.substanceUom,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeWithoutExclude,
        optionsEndpoint: endpoints.optionsCollectionAll(codePickersId.substanceUom),
        optionsParams: { minLength: 1, displayNotSelected: false }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.indicatorSubstanceCategory,
    label: translation.indicatorSubstanceCategory,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeWithoutExclude,
        optionsEndpoint: endpoints.optionsCollectionAll(codePickersId.substanceCategory),
        optionsParams: { displayNotSelected: false }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.indicatorSubstance,
    label: translation.indicatorSubstance,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeHierarchySubLevelWithCategoryHierarchical,
        categoryOptionsEndpoint: endpoints.optionsCollectionAll(codePickersId.substanceCategory),
        categoryOptionsParams: { displayNotSelected: false },
        optionsEndpoint: endpoints.optionsCollectionAll(codePickersId.substance),
        optionsParams: { displayNotSelected: false }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.indicatorSubstanceUom,
    label: translation.indicatorSubstanceUoM,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeWithoutExclude,
        optionsEndpoint: endpoints.optionsCollectionAll(codePickersId.substanceUom),
        optionsParams: { minLength: 1, displayNotSelected: false }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.factor,
    label: translation.factor,
    fields: [
      {
        fieldTypeId: fieldType.number,
        type: CustomFilterType.decimalRange,
        includeInSummary: true
      }
    ],
    type: FilterType.DECIMAL_RANGE
  },

  !!metadata?.location && {
    id: filterId.location,
    label: translation.location,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeHierarchySubLevel,
        optionsEndpoint: endpoints.optionsCollectionAll(codePickersId.location),
        optionsParams: { displayNotSelected: true }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  !!metadata?.unit && {
    id: filterId.unit,
    label: translation.unit,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeHierarchySubLevel,
        optionsEndpoint: endpoints.optionsCollectionAll(codePickersId.unit),
        optionsParams: { displayNotSelected: true }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  !!metadata?.source && {
    id: filterId.source,
    label: translation.source,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeWithoutExclude,
        optionsEndpoint: endpoints.optionsCollectionAll(codePickersId.source),
        optionsParams: { displayNotSelected: true }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  !!metadata?.equipment && {
    id: filterId.equipment,
    label: translation.equipment,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeHierarchySubLevel,
        optionsEndpoint: endpoints.optionsCollectionAll(codePickersId.equipment),
        optionsParams: { displayNotSelected: true }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.comments,
    label: translation.comments,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.text,
        includeInSummary: true
      }
    ],
    type: FilterType.TEXT
  }
].filter(f => !!f);

export const codeDefaultFilters = (
  translation: CodetableTranslation
): { [key: string]: FilterCommonValues<number>[] } => ({
  [PredefinedIds.include]: [
    { value: allPredefinedFilters.hideHistoricalFactors, description: translation.hide_historical_factors }
  ]
});

interface MapCategoryIdToSelectedIds {
  categoryId: number;
}

export const mapCategoryIdToSelectedIds = ({ categoryId }: MapCategoryIdToSelectedIds): object =>
  isDefinedIncludingZero(categoryId) ? {
    category: {
      selectedIds: [
        {
          v: categoryId
        }
      ]
    }
  } : {};
